import { useTranslation } from "@next-platform/client-common-core";
import { ListEmpty, Spinner } from "@next-platform/client-common-spicy-ui";
import { type RulebookSummary, RulebookVisibility } from "@next-platform/packages-ts-types";
import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

import RulebookCard from "./RulebookCard";

type ModalRulebookListProps = {
  rulebooks: RulebookSummary[] | null;
  visibleRulebooks: RulebookSummary[];
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  pickedId: string | null;
  onPick: (rulebookId: string) => void;
  dataTestId: string;
};

/** Body of the Rulebook picker: loading, load error, no Rulebooks yet, no search matches, or the selectable cards. */
const ModalRulebookList = ({ rulebooks, visibleRulebooks, isLoading, hasError, onRetry, pickedId, onPick, dataTestId }: ModalRulebookListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleCreate = useCallback(() => navigate(ROUTES.rulebookEditor), [navigate]);

  // A failed load or refresh shows Retry, never a possibly stale list: a Rulebook deleted elsewhere must not be offered.
  if (hasError) {
    return (
      <ListEmpty
        isLoading={false}
        title={t("rulebookEditor:attach.modal.errorTitle", "Couldn't load your Rulebooks")}
        description={t("rulebookEditor:attach.modal.errorDescription", "Check your connection and try again.")}
        buttonText={t("common:retry", "Retry")}
        onClick={onRetry}
      />
    );
  }
  if (!rulebooks) {
    return isLoading ? <Spinner className="mx-auto" /> : null;
  }
  if (rulebooks.length === 0) {
    return (
      <ListEmpty
        isLoading={false}
        title={t("rulebookEditor:attach.modal.emptyTitle", "No Rulebooks yet")}
        description={t("rulebookEditor:attach.modal.emptyDescription", "Create a Rulebook in the Rulebook Editor, then attach it here.")}
        buttonText={t("rulebookEditor:attach.modal.create", "Create a Rulebook")}
        onClick={handleCreate}
      />
    );
  }
  if (visibleRulebooks.length === 0) {
    return (
      <ListEmpty
        isLoading={false}
        title={t("common:noResultsFound")}
        description={t("rulebookEditor:attach.modal.noMatches", "No Rulebook matches your search.")}
      />
    );
  }
  return (
    <>
      {visibleRulebooks.map((rulebook) => (
        <RulebookCard
          key={rulebook.id}
          variant="selectable"
          dataTestId={`${dataTestId}-Card-${rulebook.id}`}
          id={rulebook.id}
          name={rulebook.name}
          description={rulebook.description}
          numMeters={rulebook.num_meters}
          coverUrl={getAvatarUrl(rulebook.cover_url ?? "", { preset: AvatarPreset.AVATAR_64X64 }) ?? undefined}
          isPrivate={rulebook.visibility === RulebookVisibility.PRIVATE}
          isSelected={rulebook.id === pickedId}
          onSelect={onPick}
        />
      ))}
    </>
  );
};

export default memo(ModalRulebookList);
